import { Chip } from "@mui/material";
import { diagnosticsApi, type DxModalityDto } from "@/api/diagnostics";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/radiology/modalities";
const LIST_KEY = ["dx-modalities"];

export function RadiologyModalitiesPage() {
  return (
    <MasterListPage<DxModalityDto>
      breadcrumbs={["Diagnostics", "Radiology"]}
      title="Modalities"
      subtitle="Imaging modalities (CT, MR, US…). Populated by the HealNTrack catalog sync or entered here."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listModalities(inc)}
      deleteFn={diagnosticsApi.deleteModality}
      basePath={BASE}
      newLabel="New modality"
      labelOf={(r) => r.name}
      deleteNote="It will be refused while it has procedures, machines, slots, report templates or recorded studies."
      columns={[
        { field: "code", headerName: "Code", width: 140 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 240 },
        {
          field: "usesIonizingRadiation", headerName: "Ionizing", width: 120,
          renderCell: (p) => p.row.usesIonizingRadiation
            ? <Chip size="small" label="Yes" color="warning" variant="outlined" />
            : <>—</>,
        },
        statusColumn<DxModalityDto>(),
      ]}
    />
  );
}

interface Values { code: string; name: string; usesIonizingRadiation: boolean; isActive: boolean }

export function ModalityFormPage() {
  return (
    <MasterFormPage<Values, DxModalityDto>
      breadcrumbs={["Diagnostics", "Radiology", "Modalities"]}
      noun="modality"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-modality", id]}
      defaultValues={{ code: "", name: "", usesIonizingRadiation: false, isActive: true }}
      getFn={diagnosticsApi.getModality}
      toValues={(d) => ({
        code: d.code, name: d.name,
        usesIonizingRadiation: d.usesIonizingRadiation, isActive: d.isActive,
      })}
      createFn={(v) => diagnosticsApi.createModality({
        code: v.code.trim(), name: v.name.trim(), usesIonizingRadiation: v.usesIonizingRadiation,
      })}
      updateFn={(id, v) => diagnosticsApi.updateModality(id, {
        code: v.code.trim(), name: v.name.trim(),
        usesIonizingRadiation: v.usesIonizingRadiation, isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <TextFieldCell control={control} name="code" label="Code" span={4} required />
          <TextFieldCell control={control} name="name" label="Name" span={8} required />
          <SwitchCell control={control} name="usesIonizingRadiation" label="Uses ionizing radiation" />
          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
